import {
    Center,
    Combobox,
    ComboboxClearButton,
    Flex,
    Group,
    InputBase,
    MantineStyleProps,
    ScrollArea,
    Stack,
    Text,
    useCombobox
} from "@mantine/core";
import TABLER_ICONS_LIST, {ITablerIconMetadata} from "@/components/icon_picker/TablerIconsList.ts";
import type {StandardLonghandProperties} from 'csstype'
import React, {useRef, useState} from "react";

interface ITablerWebfontIconProps {
    icon: string
    size?: StandardLonghandProperties["fontSize"]
    color?: StandardLonghandProperties["color"]
}

/**
 * React component to dynamically display Tabler Icons
 * @param props the icon and style properties
 * @constructor
 */
export function TablerWebfontIcon(props: ITablerWebfontIconProps) {
    return <i className={`ti ti-${props.icon}`} style={{fontSize: props.size ?? '1em', color: props.color ?? 'inherit'}}/>
}

// Minimum number of characters before a search is performed
const SEARCH_QUERY_MIN_CHARS = 3


/**
 * Get Tabler Icon options matching a search query from a specified set
 * @param data the initial set to search from
 * @param searchQuery the search query to match
 */
function getFilteredOptions(data: ITablerIconMetadata[], searchQuery: string) {
    const result: ITablerIconMetadata[] = []

    if (searchQuery.length < SEARCH_QUERY_MIN_CHARS) // Do not perform filtering until min characters are met
        return result

    for (const item of data) {
        if (item.n.toLowerCase().includes(searchQuery.trim().toLowerCase()) || item.t.toLowerCase().includes(searchQuery.trim().toLowerCase())) {
            result.push(item)
        }
    }

    return result
}

export interface ITablerIconPickerProps extends MantineStyleProps {
    value?: string | null
    onChange: (value: string | undefined | null) => void
    label?: string
    error?: string
    required?: boolean
}

/**
 * React component to select a Tabler icon
 * @param props the value and onChange handler, as well as display properties
 * @constructor
 */
export default function TablerIconPicker(props: ITablerIconPickerProps) {
    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption()
    })

    const [search, setSearch] = useState('')
    const inputRef = useRef<HTMLInputElement | null>(null)


    const filteredOptions = getFilteredOptions(TABLER_ICONS_LIST, search)

    const options = filteredOptions.map((item) => (
        <Combobox.Option value={item.n} key={item.n} w={"15em"}>
            <Stack justify={"center"} align={"center"}>
                <TablerWebfontIcon icon={item.n} size={"2em"}/>
                <Text>{item.n}</Text>
            </Stack>
        </Combobox.Option>
    ))

    const searchRan = search.length >= SEARCH_QUERY_MIN_CHARS
    const resultsEmpty = options.length == 0
    const {value, onChange, ...buttonProps} = props


    /**
     * Run when text input is focused or clicked
     * Open the dropdown and set focus to the updated (button->text input) field
     */
    function onInputFocus() {
        combobox.openDropdown()
        setTimeout(() => inputRef.current?.focus(), 100)
    }

    /**
     * Run when button input is focused or clicked
     * Close the dropdown, clear search, clear selected icon, focus to the updated (button->text input) field
     */
    function onButtonFocus() {
        combobox.openDropdown()
        setSearch(value ?? '')
        onChange(null)
        setTimeout(() => inputRef.current?.focus(), 100)
    }

    /**
     * Run when an icon option is selected from the dropdown
     * Set search to the name of the selected option, send the option to parent component, close the dropdown
     * @param option
     */
    function onOptionSelected(option: string) {
        onChange(option)
        setSearch(option)
        combobox.closeDropdown()
    }

    /**
     * Run when the search query input is changed
     * Open the dropdown, update the list of options and set the search query
     * @param e the onChange event fired by the search input
     */
    function onSearchUpdated(e: React.ChangeEvent<HTMLInputElement>) {
        combobox.openDropdown()
        combobox.updateSelectedOptionIndex()
        setSearch(e.currentTarget.value)
    }

    /**
     * Run when the text input loses focus
     * Close the dropdown, find the icon matching the current search query (if exists) and send it to parent (if exists)
     */
    function onInputBlur() {
        combobox.closeDropdown()
        for (const item of filteredOptions) {
            if (item.n == search) {
                onChange(item.n)
            }
        }
    }

    return <Combobox store={combobox} withinPortal={true} onOptionSubmit={onOptionSelected}>
        <Combobox.Target>
            {value === null
                ? <InputBase
                    {...props}
                    rightSection={<Combobox.Chevron/>}
                    value={search}
                    onChange={onSearchUpdated}
                    onClick={onInputFocus}
                    onFocus={onInputFocus}
                    onBlur={onInputBlur}
                    ref={inputRef}
                    placeholder={"Select an Icon"}
                    rightSectionPointerEvents={"none"}
                />
                : <InputBase
                    {...buttonProps}
                    component={"button"}
                    type={"button"}
                    rightSection={<ComboboxClearButton onClear={() => {onChange(null); setSearch("")}} aria-label={"Clear value"}/>}
                    multiline={true}
                    onClick={onButtonFocus}
                    onFocus={onButtonFocus}
                    rightSectionPointerEvents={"all"}
                >
                    {value && <Group align={"center"}><TablerWebfontIcon icon={value} size={"3em"}/><Text>{value}</Text></Group>}
                </InputBase>
            }
        </Combobox.Target>

        <Combobox.Dropdown>
            <Combobox.Options>
                {(searchRan && !resultsEmpty)
                    ? <ScrollArea h={resultsEmpty ? "2em" : "17.5em"}>
                        <Center>
                            <Flex wrap={"wrap"} justify={"center"} w={"100%"}>
                                {options}
                            </Flex>
                        </Center>
                    </ScrollArea>
                    : <Combobox.Empty>
                        {searchRan ? "Nothing found" : `Please enter ${SEARCH_QUERY_MIN_CHARS} or more characters to search icons`}
                    </Combobox.Empty>}
            </Combobox.Options>
        </Combobox.Dropdown>
    </Combobox>
}
